<template>
  <v-card flat class="rounded-xl overflow-hidden pa-4 pa-md-8">
    <bulk-download-qr-modal
      v-model="showBulkDownloadModal"
      :items="selectedItems"
      zip-prefix="qrcodes"
    />

    <!-- بنر زنده نمایش پروسس ساخت لیبل QR -->
    <v-expand-transition>
      <div v-if="activeBgTasks.length > 0" class="mb-4">
        <v-alert
          v-for="task in activeBgTasks"
          :key="task.id || task.productId"
          color="primary"
          dark
          dense
          class="mb-2 rounded-lg elevation-2"
        >
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="d-flex align-center">
              <v-progress-circular
                indeterminate
                size="18"
                width="2"
                color="white"
                class="ml-2"
              />
              <span class="font-weight-bold">
                در حال ساخت کیوآر: {{ task.title || 'محصول' }}
              </span>
              <span
                v-if="task.currentTitle || task.itemTitle"
                class="text-caption mr-2 opacity-80"
              >
                ({{ task.currentTitle || task.itemTitle }})
              </span>
            </div>

            <!-- شمارنده مثلا: 4 از 120 (3%) -->
            <div class="text-caption font-weight-bold d-flex align-center">
              <span class="dir-ltr ml-1">{{ task.current }} / {{ task.total }}</span>
              <span>({{ task.percent || Math.round(((task.current || 0) / (task.total || 1)) * 100) }}%)</span>
            </div>
          </div>

          <v-progress-linear
            :value="task.percent || Math.round(((task.current || 0) / (task.total || 1)) * 100)"
            height="7"
            rounded
            striped
            color="amber"
            background-color="white opacity-30"
          />
        </v-alert>
      </div>
    </v-expand-transition>

    <!-- جدول محصولات فیزیکی -->
    <vue-table
      v-model="selectedItems"
      :data="tableData"
      :headers="headers"
      :loading="loading || bulkLoading"
      @change="onPagination"
      item-key="id"
      show-select
    >
      <!-- بخش فیلترهای بالا + دکمه اکشن گروهی -->
      <template v-slot:filters="{ props, perPage }">
        <v-row no-gutters class="mb-4 align-center">
          <v-col class="pa-2" cols="12" lg="2" md="3">
            <v-select
              v-model="props.length"
              :items="perPage"
              class="rounded-lg"
              dense
              hide-details
              label="تعداد در هر صفحه"
              outlined
              @change="() => onPagination(props)"
            />
          </v-col>
          <v-col class="pa-2" cols="12" lg="3" md="4">
            <v-text-field
              v-model="props.search"
              append-icon="mdi-magnify"
              label="جستجو در عنوان یا شناسه..."
              outlined
              dense
              hide-details
              clearable
              class="rounded-lg"
              @keyup.enter="() => onPagination(props)"
              @click:clear="() => { props.search = null; onPagination(props); }"
            />
          </v-col>

          <!-- دکمه تازه‌سازی دستی -->
          <v-col class="pa-2" cols="12" lg="2" md="2">
            <v-btn
              class="btn-buy rounded-lg"
              :loading="loading"
              @click="fetch"
            >
              <v-icon small left>mdi-refresh</v-icon>
              بروزرسانی لیست
            </v-btn>
          </v-col>

          <!-- دکمه دانلود گروهی -->
          <v-col class="pa-2 d-flex justify-end" cols="12" lg="5" md="3">
            <v-slide-x-reverse-transition>
              <v-btn
                v-if="selectedItems && selectedItems.length > 0"
                class="btn-download rounded-lg elevation-2 px-4 font-weight-bold"
                :loading="bulkLoading"
                @click="bulkDownload"
              >
                <v-icon left>mdi-folder-download</v-icon>
                دانلود گروهی کیوآرها ({{ selectedItems.length }} مورد)
              </v-btn>
            </v-slide-x-reverse-transition>
          </v-col>
        </v-row>
      </template>

      <!-- ستون ردیف -->
      <template v-slot:index="{ item }">
        {{ persian(item.index + 1 + (tableData.current_page - 1) * tableData.per_page) }}
      </template>

      <!-- ستون لوگو / تصویر QR -->
      <template v-slot:logo="{ item }">
        <div class="d-flex flex-column align-center justify-center pa-1">
          <v-avatar size="52" rounded="lg" class="elevation-1 border mb-1">
            <v-img :src="item.img_url || item.image" contain :alt="item.title">
              <template v-slot:placeholder>
                <v-icon color="grey lighten-1">mdi-image-outline</v-icon>
              </template>
            </v-img>
          </v-avatar>

          <!-- دکمه دانلود تکی مستقیم زیر تصویر -->
          <v-btn
            x-small
            outlined
            class="btn-download rounded-lg px-2 mt-1 qr-quick-dl-btn"
            @click.stop="openDownloadModal(item)"
          >
            <v-icon x-small left>mdi-download</v-icon>
            دانلود QR
          </v-btn>
        </div>
      </template>

      <!-- عنوان و شناسه اختصاصی -->
      <template v-slot:product_info="{ item }">
        <div class="d-flex flex-column align-start text-right py-2">
          <div class="d-flex align-center gap-2 mb-1">
            <span class="product-title font-weight-bold">{{ item.title || 'بدون نام' }}</span>
          </div>
          <div class="d-flex align-center gap-2 grey--text text-caption">
            <a
              :href="getQrUrl(item)"
              target="_blank"
              rel="noopener noreferrer"
              class="identifier-link d-inline-flex align-center text-decoration-none"
            >
              <span class="identifier-code dir-ltr">{{ getQrUrl(item) }}</span>
              <v-icon x-small color="primary" class="mr-1">mdi-open-in-new</v-icon>
            </a>
            <v-btn 
              icon 
              x-small 
              @click.stop="copyText(getQrUrl(item))" 
              color="grey darken-1"
              title="کپی لینک"
            >
              <v-icon x-small>mdi-content-copy</v-icon>
            </v-btn>
          </div>
        </div>
      </template>

      <!-- اکشن‌ها و دکمه‌ها -->
      <template v-slot:action="{ item }">
        <div class="d-flex align-center justify-center gap-1" @click.stop.prevent>
          <action-buttons :data="item" :meta="getMetaButtons(item)" />
        </div>
      </template>
    </vue-table>

    <!-- مدال دانلود تکی کیوآر -->
    <custom-popup
      v-model="showDownloadModal"
      :cons="{ title: 'دانلود کیوآر کد: ' + (selectedItem ? selectedItem.title : ''), icon: 'download' }"
      max-width="650"
      hide-confirm
      @close="showDownloadModal = false"
    >
      <download-file-with-api
        v-if="showDownloadModal && selectedItem"
        :address="`${selectedItem.domain && selectedItem.domain.name ? selectedItem.domain.name : 'qrland.in/'}${selectedItem.identifier}`"
        :visit-name="selectedItem.title || 'qr'"
        :qr_id="String(selectedItem.id)"
        :is-qrlink="false"
        :preloaded-info="selectedItem"
        @close="showDownloadModal = false"
      />
    </custom-popup>

    <!-- مدال ویرایش اطلاعات و لوگوی محصول فیزیکی -->
    <custom-popup
      v-model="showEditModal"
      :cons="{ title: 'ویرایش اطلاعات و عکس محصول: ' + (selectedItem ? selectedItem.title : ''), icon: 'pencil' }"
      max-width="640"
      hide-confirm
      @close="showEditModal = false"
    >
      <edit-physical-product-modal
        v-if="showEditModal"
        :product-data="selectedItem"
        @saved="onItemUpdated"
        @close="showEditModal = false"
      />
    </custom-popup>

    <!-- مدال اتصال به کالا -->
    <custom-popup
      v-model="showLinkModal"
      :cons="{ title: 'اتصال به کالا: ' + (selectedItem ? selectedItem.title : '') }"
      max-width="800"
      hide-confirm
      @close="showLinkModal = false"
    >
    </custom-popup>

    <!-- مدال قیمت‌گذاری هوشمند -->
    <custom-popup
      v-model="showPricingModal"
      :cons="{ title: 'قیمت‌گذاری هوشمند محصول' }"
      max-width="750"
      hide-confirm
      @close="showPricingModal = false"
    >
      <quick-pricing-modal
        v-if="showPricingModal"
        :product-data="selectedItem"
        @saved="onPricingSaved"
        @close="showPricingModal = false"
      />
    </custom-popup>
  </v-card>
</template>

<script>
import ActionButtons from "@/view/components/adapters/qrList/ordersListButton.vue";
import EditPhysicalProductModal from "./EditPhysicalProductModal.vue";
import QuickPricingModal from "./QuickPricingModal.vue";
import DownloadFileWithApi from "../../components/downloadFileWithApi.vue";
import BulkDownloadQrModal from "../../components/BulkDownloadQrModal.vue";
import qrQueueDefault, { qrQueue as qrQueueNamed } from "@/services/qrQueue";

const baseQueue = (typeof window !== "undefined" && window.__QR_QUEUE_INSTANCE__) 
  ? window.__QR_QUEUE_INSTANCE__ 
  : (qrQueueDefault || qrQueueNamed);

export default {
  name: "PhysicalProductList",
  components: {
    ActionButtons,
    EditPhysicalProductModal,
    QuickPricingModal,
    DownloadFileWithApi,
    BulkDownloadQrModal,
  },
  data: () => ({
    showBulkDownloadModal: false,
    loading: false,
    bulkLoading: false,
    showEditModal: false,
    showLinkModal: false,
    showPricingModal: false,
    showDownloadModal: false,
    selectedItem: null,
    selectedItems: [],
    // آبجکت تسک‌ها در دیتای کامپوننت برای تضمین ری‌اکتیویتی در Vue 2
    bgTasks: {},
    tableProps: {
      page: 1,
      length: 10,
      search: null,
      sort: "desc",
      type: "pricelabel",
    },
    tableData: {
      data: [],
      total: 0,
      current_page: 1,
      per_page: 10,
    },
    headers: [
      { value: "index", text: "ردیف", sortable: false, align: "center" },
      { value: "logo", text: "تصویر", sortable: false, align: "center" },
      { value: "product_info", text: "مشخصات محصول", sortable: false, align: "right" },
      { value: "action", text: "عملیات", sortable: false, align: "center" },
    ],
  }),
  computed: {
    queue() {
      return (typeof window !== "undefined" && window.__QR_QUEUE_INSTANCE__)
        ? window.__QR_QUEUE_INSTANCE__
        : baseQueue;
    },
    activeBgTasks() {
      return Object.values(this.bgTasks).filter(
        (t) => t && (t.status === "running" || t.status === "processing")
      );
    },
  },
  watch: {
    // هرگاه تمام تسک‌ها تمام و غیرفعال شدند، لیست جدول تازه می‌شود
    activeBgTasks(newList, oldList) {
      if (oldList && oldList.length > 0 && newList.length === 0) {
        this.fetch();
      }
    },
  },
  mounted() {
    this.syncInitialTasks();
    this.bindQueueListeners();
    this.fetch();
  },
  beforeDestroy() {
    this.unbindQueueListeners();
  },
  methods: {
    syncInitialTasks() {
      if (this.queue && this.queue.tasks) {
        this.bgTasks = { ...this.queue.tasks };
      }
    },
    onTaskUpdated(task) {
      if (!task || !task.id) {
        if (this.queue && this.queue.tasks) {
          this.bgTasks = { ...this.queue.tasks };
        }
        return;
      }
      this.$set(this.bgTasks, task.id, { ...task });
      
      // اگر تسک موفق تمام شد
      if (task.status === "completed" || task.status === "finished") {
        this.fetch();
      }
    },
    bindQueueListeners() {
      if (this.queue && typeof this.queue.on === "function") {
        this.queue.on("task-updated", this.onTaskUpdated);
        this.queue.on("task-finished", this.onTaskUpdated);
      }
    },
    unbindQueueListeners() {
      if (this.queue && typeof this.queue.off === "function") {
        this.queue.off("task-updated", this.onTaskUpdated);
        this.queue.off("task-finished", this.onTaskUpdated);
      }
    },
    bulkDownload() {
      if (!this.selectedItems || !this.selectedItems.length) {
        this.$toast.warning("لطفاً حداقل یک محصول را از جدول انتخاب کنید");
        return;
      }
      this.showBulkDownloadModal = true;
    },
    getQrUrl(item) {
      if (!item) return "";
      const domain = (item.domain && item.domain.name) ? item.domain.name : "qrland.in";
      return `https://${domain}/${item.identifier}`;
    },
    getMetaButtons(item) {
      return {
        buttons: [
          {
            name: "downloadQr",
            label: "دانلود کیوآر",
            icon: "download",
            class: "btn-download rounded-pill",
            action: () => this.openDownloadModal(item),
          },
          {
            name: "edit",
            label: "ویرایش",
            icon: "pencil",
            class: "btn-download rounded-pill",
            action: () => this.openEditModal(item),
          },
          {
            name: "pricing",
            label: "قیمت‌گذاری",
            icon: "cash-sync",
            class: "btn-get-code rounded-pill",
            action: () => this.openPricingModal(item),
          },
          {
            name: "link",
            label: "اتصال به محصول",
            icon: "link-variant-plus",
            class: "btn-buy rounded-pill",
            action: () => this.openLinkModal(item),
          },
          {
            name: "previewQr",
            label: "نمایش در کیوآر",
            icon: "qrcode-scan",
            class: "btn-show rounded-pill",
            action: () => this.previewQr(item),
          },
        ],
      };
    },
    openDownloadModal(item) {
      this.selectedItem = item;
      this.showDownloadModal = true;
    },
    openEditModal(item) {
      this.selectedItem = item;
      this.showEditModal = true;
    },
    openPricingModal(item) {
      this.selectedItem = item;
      this.showPricingModal = true;
    },
    openLinkModal(item) {
      this.selectedItem = item;
      this.showLinkModal = true;
    },
    previewQr(item) {
      const domain = (item.domain && item.domain.name) ? item.domain.name : "qrland.in";
      const url = `https://core-qr.terminalads.com/#/qr/labelprice/${domain}/${item.identifier}`;
      window.open(url, "_blank");
    },
    async fetch() {
      this.loading = true;
      try {
        const res = await this.$api.get(`${this.$qrApi}/v1/testQr/user`, {
          params: {
            ...this.tableProps,
            fields: [
              "id", "title", "type", "domain_id", "identifier",
              "clicks", "img_url", "image", "dest_link", "expire_at",
              "created_at", "updated_at"
            ],
          },
        });
        this.tableData = res.data.data || res.data;
      } catch (error) {
        this.$toast.error("خطا در بارگذاری لیست محصولات");
      } finally {
        this.loading = false;
      }
    },
    onPagination(props) {
      if (props && typeof props === "object") {
        this.tableProps = { ...this.tableProps, ...props };
      }
      this.fetch();
    },
    onItemUpdated() {
      this.showEditModal = false;
      this.fetch();
    },
    onPricingSaved() {
      this.showPricingModal = false;
      this.fetch();
    },
    onProductLinked() {
      this.showLinkModal = false;
      this.fetch();
    },
    copyText(text) {
      if (!text) return;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(String(text)).then(() => {
          this.$toast.success("آدرس کپی شد");
        });
      }
    },
  },
};
</script>

<style scoped>
.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
.gap-4 { gap: 16px; }

.identifier-link {
  transition: opacity 0.2s;
}
.identifier-link:hover {
  opacity: 0.8;
}

.identifier-code {
  background: #f1f3f5;
  color: #2b6cb0;
  padding: 3px 8px;
  border-radius: 6px;
  font-family: monospace;
  font-size: 0.82rem;
  border: 1px dashed #cbd5e0;
}
.dir-ltr {
  direction: ltr;
  display: inline-block;
}

.product-title {
  font-size: 0.95rem;
  color: #2b2b2b;
}

.opacity-80 {
  opacity: 0.85;
}
</style>
